/** Fixed vocabulary for the import mapper (spec §4). Keep every list closed and explicit —
 * an unlisted key must fall through to "ignored, with a warning", never to "applied" (spec Risks:
 * "Claude Code moves"). */

export const MAX_IMPORT_HOPS = 4;
export const INSTRUCTIONS_CHAR_CAP = 20_000;
export const SUBAGENT_PROMPT_CAP = 10_000;

/** Never accepted into `allowedTools`, approved or not (spec §4.3). Write access comes only from `allowedAreas`. */
export const FORBIDDEN_WRITE_TOOLS = ["edit", "write", "multiedit", "notebookedit"] as const;

/** Read/research tools that are safe to allow verbatim regardless of specifier. */
export const SAFE_ALLOW_TOOL_NAMES = ["read", "glob", "grep", "webfetch", "websearch", "todowrite"] as const;

export const TASK_AGENT_TOOL_NAMES = ["task", "agent"] as const;

export const MCP_TOOL_PREFIX = "mcp__";

export const RESERVED_MCP_SERVER_NAME = "roomate";
export const MCP_SERVER_NAME_REGEX = /^[a-z0-9_-]{1,40}$/;

/** A leading or trailing run of non-alphanumerics (a stray space, `-`, `_`, `.`, ...) must not turn
 * the reserved name into something that merely *looks* different while still reading as "roomate"
 * to a human skimming the preview — so this strips both ends before comparing. `MCP_SERVER_NAME_REGEX`
 * allows a leading `-`/`_`, so a leading-only strip (the original form of this check) let a decoy
 * server named `-roomate` or `_roomate` pass as distinct, reaching `--mcp-config` and `mcp__-roomate`/
 * `mcp___roomate` allow rules undetected (task #50 review finding). Used when the name is first seen
 * (`map.ts`), again defensively on approval (`approve.ts`), and again in `buildRunPlan`'s own
 * re-check (`apps/server/src/agent/run-config.ts`). */
export function isReservedServerName(name: string): boolean {
  return name.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "") === RESERVED_MCP_SERVER_NAME;
}

export const SUBAGENT_NAME_REGEX = /^[a-z0-9][a-z0-9_-]{0,39}$/;
export const RESERVED_SUBAGENT_NAMES = ["researcher", "test-runner", "reviewer"] as const;

/** Imported `defaultMode` / `permissionMode` values that are never applied (spec §4.3). */
export const NEVER_IMPORTED_PERMISSION_MODES = ["bypasspermissions", "auto", "dontask", "acceptedits"] as const;

/** Settings/permissions keys that are ignored no matter what (spec §4.3, §4.8). */
export const NEVER_IMPORTED_SETTINGS_KEYS = [
  "additionalDirectories",
  "apiKeyHelper",
  "enabledPlugins",
  "extraKnownMarketplaces",
  "enableAllProjectMcpServers",
  "enabledMcpjsonServers",
  "statusLine",
] as const;

/** Known agent frontmatter keys (spec §1). Anything else is an unknown key: ignored, with a warning. */
export const KNOWN_AGENT_FRONTMATTER_KEYS = [
  "name",
  "description",
  "tools",
  "disallowedTools",
  "model",
  "permissionMode",
  "maxTurns",
  "skills",
  "mcpServers",
  "hooks",
  "memory",
  "effort",
  "isolation",
  "background",
  "omitClaudeMd",
  "initialPrompt",
  "color",
] as const;

/** Subagent keys dropped with a warning even for the main agent's own subagents (spec §4.4). */
export const DROPPED_SUBAGENT_KEYS = ["hooks", "mcpServers", "permissionMode", "memory", "isolation", "background", "initialPrompt"] as const;

/** Known skill frontmatter keys (spec §1). */
export const KNOWN_SKILL_FRONTMATTER_KEYS = [
  "name",
  "description",
  "when_to_use",
  "allowed-tools",
  "disallowed-tools",
  "model",
  "effort",
  "context",
  "agent",
  "hooks",
  "paths",
  "disable-model-invocation",
  "user-invocable",
] as const;

/** Known `.claude/settings.json` keys (spec §1). */
export const KNOWN_SETTINGS_KEYS = [
  "permissions",
  "env",
  "hooks",
  "model",
  "disableAllHooks",
  "apiKeyHelper",
  "outputStyle",
  "enabledPlugins",
  "extraKnownMarketplaces",
  "enableAllProjectMcpServers",
  "enabledMcpjsonServers",
] as const;

/** Shell-injection patterns Claude Code evaluates in a skill body even without workspace trust (spec §2, §4.7). */
export const SKILL_SHELL_INJECTION_PATTERNS = [/!`[^`]*`/, /(^|\n)\s*```!/] as const;

/** Starts from `apps/server/src/agent/run-config.ts`'s `assertNoRawSecrets` prefix list — so core
 * and server agree on the common cases — then adds a few more well-known token shapes an untrusted
 * import is realistically carrying (GitLab, Google, Slack, npm, Stripe, SendGrid) plus embedded
 * DB-connection-string credentials, which name no "key"-looking field at all. This makes core's own
 * check a strict superset of the server's, never a subset: broadening only here is the safe
 * direction, because core rejecting something the server would have allowed is a false-positive
 * import error, not a hole. (Review finding, agent-import task #48 — see record_decision.) */
export const SECRET_LIKE =
  /^(sk-|ghp_|github_pat_|xox[bp]-|rm_|AKIA|eyJ[A-Za-z0-9_-]{20,}|glpat-|AIza[0-9A-Za-z_-]{10,}|xapp-|npm_|sk_live_|pk_live_|SG\.[\w-]{10,}\.[\w-]{10,})/;
/** `scheme://user:password@host` with the password captured separately, so a literal credential
 * can be told apart from a `${VAR}` reference in that position (see `secrets.ts`). */
export const DB_CONNECTION_STRING_RE = /^(postgres(ql)?|mysql|mongodb(\+srv)?|redis):\/\/[^/@\s:]+:([^/@\s]+)@/i;
export const SECRET_KEY = /(token|secret|key|password|authorization|credential)/i;
export const ENV_REF = /^\$\{[A-Z_][A-Z0-9_]*(:-[^}]*)?\}$/;

/** Env keys that are never applied even when a user approves them (spec §4.8). */
export const ENV_DENYLIST_PATTERNS = [
  /^ANTHROPIC_/,
  /^CLAUDE_CODE_/,
  /^CLAUDE_/,
  /^ROOMATE_/,
  /^OTEL_/,
  /^ENABLE_TOOL_SEARCH$/,
  /^PATH$/,
  /^NODE_OPTIONS$/,
  /^LD_/,
  /^DYLD_/,
  /^GIT_/,
  /^HOME$/,
  /^SHELL$/,
  /^BASH_ENV$/,
  /^ENV$/,
  /^ZDOTDIR$/,
  /^PROMPT_COMMAND$/,
  /^IFS$/,
] as const;

export function isNeverImportedEnvKey(key: string): boolean {
  return ENV_DENYLIST_PATTERNS.some((re) => re.test(key));
}
